'use client';

import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { flushSync } from 'react-dom';

import { css, cx } from '@/styled-system/css';
import { usePathname, useRouter } from '@/src/i18n/navigation';
import { emitTrackToggleViewModeEvent } from '@/src/analytics/hotpads/events/srp';
import {
  contentPanel,
  contentPanelChildren,
  contentPanelDesktopBottomInset,
  contentPanelMobileMapActive,
  mapFallbackContainer,
  mapFallbackText,
  mapPanel,
  mapPanelMobileActive,
  mobileMapSearchBarOverlay,
  searchLayout,
} from '@/src/layouts/persistentSearchLayout.styles';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import type { StaticMapBounds } from '@/src/lib/utils/staticMap';
import { parseMapPositionFromSearchParams } from '@/src/lib/map/viewportBounds';
import { SRP_LISTINGS_VIEW } from '../_srp/_data/srpListingsView';
import { StaticMapPreview } from './StaticMapPreview';
import { useDeferredMapIsland } from './useDeferredMapIsland';
import { useSetActiveListing, useSetMapHoveredMarker, useSetMapPinnedMarker } from '@/src/lib/hooks';
import { restoreQueuedSearchHdpScroll } from '@/src/lib/navigation/searchHdpHistory';
import {
  clearPendingMobileFeedRestoreScrollY,
  setPendingMobileFeedRestoreScrollY,
} from '@/src/lib/navigation/mobileFeedTransitionScroll';
import { MobileSrpBottomControls, type MobileSrpControlValue } from './MobileSrpBottomControls';
import { useSrpViewMode } from './SearchViewModeContext';

type Props = {
  children: ReactNode;
  stickySearchBar?: ReactNode;
  mobileMapSearchBar?: ReactNode;
  mapPreviewBounds?: StaticMapBounds;
};

// Desktop-only chrome (display none below lg keeps it off the mobile map's
// touch path); while opaque it intercepts pointer events over the live map.
const mapPreviewOverlay = css({
  display: { base: 'none', lg: 'block' },
  position: 'absolute',
  inset: 0,
  zIndex: 'zIndexOverlay',
  opacity: 1,
  transitionProperty: 'opacity',
  // Matches MAP_PREVIEW_FADE_OUT_MS in useDeferredMapIsland.
  transitionDuration: '400ms',
  transitionTimingFunction: 'ease',
  '&[data-map-ready="true"]': {
    opacity: 0,
    pointerEvents: 'none',
  },
});

type ViewTransitionDocument = Document & {
  startViewTransition?: (updateCallback: () => void) => void;
};

const SearchMapIsland = dynamic(() => import('./SearchMapIsland').then((m) => m.SearchMapIsland), {
  ssr: false,
  loading: () => (
    <div className={mapFallbackContainer}>
      <p className={mapFallbackText}>Loading map…</p>
    </div>
  ),
});

function isHdpPath(pathname: string) {
  const finalSegment = pathname.split('/').filter(Boolean).at(-1);
  return finalSegment === 'pad' || finalSegment === 'building';
}

function removeViewParam(pathname: string, searchParams: URLSearchParams) {
  const nextParams = new URLSearchParams(searchParams.toString());
  nextParams.delete('view');
  const qs = nextParams.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

function canStartViewTransition(): boolean {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
  return typeof (document as ViewTransitionDocument).startViewTransition === 'function';
}

function runViewTransition(update: () => void) {
  if (!canStartViewTransition()) {
    update();
    return;
  }

  (document as ViewTransitionDocument).startViewTransition?.(() => {
    flushSync(update);
  });
}

function getWindowScrollY(): number {
  return window.scrollY || window.pageYOffset || 0;
}

export function PersistentSearchShell({ children, stickySearchBar, mobileMapSearchBar, mapPreviewBounds }: Props) {
  const isDesktop = useIsDesktop();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { viewMode, setViewMode } = useSrpViewMode();
  const hdpRoute = isHdpPath(pathname);
  const mobileMapScrollYRef = useRef<number | null>(null);
  const pendingMobileMapScrollRestoreRef = useRef<number | null>(null);
  const pendingMobileFeedFrameRef = useRef(0);
  const pendingMobileFeedTokenRef = useRef(0);
  const wasHdpRouteRef = useRef(hdpRoute);
  const [isTransitioningMobileMapToList, setIsTransitioningMobileMapToList] = useState(false);
  const [isOpeningMobileMap, setIsOpeningMobileMap] = useState(false);
  const setMapPinnedMarker = useSetMapPinnedMarker();
  const setMapHoveredMarker = useSetMapHoveredMarker();
  const setActiveListing = useSetActiveListing();
  const mobileMapActiveFromUrl = !isDesktop && !hdpRoute && searchParams.get('view') === 'map';
  const mobileMapActive = (mobileMapActiveFromUrl || isOpeningMobileMap) && !isTransitioningMobileMapToList;
  const shouldRenderMap = isDesktop || mobileMapActive;
  // Skip the preview when the URL seeds a viewport, per the island's own parse.
  const [hasExplicitViewportUrl] = useState(
    () => parseMapPositionFromSearchParams(searchParams, SRP_LISTINGS_VIEW) !== null,
  );
  const { showIsland, showPreview, isMapReady, onActivate, onMapReady, onPreviewError } = useDeferredMapIsland({
    hasPreview: Boolean(mapPreviewBounds) && !hasExplicitViewportUrl,
    shouldRenderMap,
    mobileMapActive,
    pathname,
  });
  const shouldShowMobileControls = !isDesktop && !hdpRoute;
  const effectiveViewMode = isDesktop && viewMode === 'feed' ? 'grid' : viewMode;
  const mobileControlValue: MobileSrpControlValue = mobileMapActive ? 'map' : effectiveViewMode;

  useEffect(() => {
    if (!hdpRoute || searchParams.get('view') !== 'map') return;
    router.replace(removeViewParam(pathname, searchParams), { scroll: false });
  }, [hdpRoute, pathname, router, searchParams]);

  useLayoutEffect(() => {
    if (hdpRoute) return;
    restoreQueuedSearchHdpScroll(window.location.href);
  }, [hdpRoute, pathname]);

  useLayoutEffect(() => {
    if (isDesktop && viewMode === 'feed') {
      setViewMode('grid');
    }
  }, [isDesktop, setViewMode, viewMode]);

  useEffect(() => {
    if (wasHdpRouteRef.current && !hdpRoute) {
      setMapPinnedMarker(null);
      setMapHoveredMarker(null);
      setActiveListing(null);
    }

    wasHdpRouteRef.current = hdpRoute;
  }, [hdpRoute, setActiveListing, setMapHoveredMarker, setMapPinnedMarker]);

  useLayoutEffect(() => {
    if (mobileMapActive) return;
    const scrollY = pendingMobileMapScrollRestoreRef.current;
    if (scrollY == null) return;

    pendingMobileMapScrollRestoreRef.current = null;
    window.scrollTo(0, scrollY);
    window.requestAnimationFrame(() => window.scrollTo(0, scrollY));
  }, [mobileMapActive]);

  useEffect(() => {
    if (mobileMapActiveFromUrl) {
      setIsOpeningMobileMap(false);
      return;
    }

    setIsOpeningMobileMap(false);
    setIsTransitioningMobileMapToList(false);
  }, [mobileMapActiveFromUrl]);

  useEffect(() => {
    return () => {
      pendingMobileFeedTokenRef.current += 1;
      if (pendingMobileFeedFrameRef.current) {
        window.cancelAnimationFrame(pendingMobileFeedFrameRef.current);
        pendingMobileFeedFrameRef.current = 0;
      }
    };
  }, []);

  const cancelPendingMobileFeedTransition = useCallback(() => {
    pendingMobileFeedTokenRef.current += 1;
    clearPendingMobileFeedRestoreScrollY();
    if (pendingMobileFeedFrameRef.current) {
      window.cancelAnimationFrame(pendingMobileFeedFrameRef.current);
      pendingMobileFeedFrameRef.current = 0;
    }
  }, []);

  const openMobileMap = useCallback(() => {
    mobileMapScrollYRef.current = window.scrollY || window.pageYOffset || 0;
    const showMapLayout = () => {
      setIsTransitioningMobileMapToList(false);
      setIsOpeningMobileMap(true);
      setViewMode('grid');
      setMapPinnedMarker(null);
      setActiveListing(null);
    };

    // Deliberately not wrapped in `runViewTransition`, unlike `closeMobileMap`:
    // snapshotting the full listings document cost ~120ms of INP to animate into a
    // fullscreen panel. The return trips animate back to that long document, where
    // the snapshot is what keeps the relayout off the interaction (HP-8727).
    showMapLayout();

    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.set('view', 'map');
    const qs = nextParams.toString();
    router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [pathname, router, searchParams, setActiveListing, setMapPinnedMarker, setViewMode]);

  const setViewModeWithTransition = useCallback(
    (nextViewMode: typeof viewMode) => {
      runViewTransition(() => {
        setViewMode(nextViewMode);
      });
    },
    [setViewMode],
  );

  const closeMobileMap = useCallback(() => {
    const nextUrl = removeViewParam(pathname, searchParams);
    const showListLayout = () => {
      pendingMobileMapScrollRestoreRef.current = mobileMapScrollYRef.current;
      setMapPinnedMarker(null);
      setActiveListing(null);
      setIsOpeningMobileMap(false);
      setIsTransitioningMobileMapToList(true);
    };

    runViewTransition(showListLayout);
    router.replace(nextUrl, { scroll: false });
  }, [pathname, router, searchParams, setActiveListing, setMapPinnedMarker]);

  const switchToFeedAfterWindowTop = useCallback(() => {
    const restoreScrollY = getWindowScrollY();
    if (restoreScrollY <= 1) {
      clearPendingMobileFeedRestoreScrollY();
      setViewModeWithTransition('feed');
      return;
    }

    setPendingMobileFeedRestoreScrollY(restoreScrollY);
    const transitionToken = pendingMobileFeedTokenRef.current;
    let attempts = 0;

    const tick = () => {
      if (transitionToken !== pendingMobileFeedTokenRef.current) return;

      window.scrollTo(0, 0);
      attempts += 1;

      if (getWindowScrollY() <= 1 || attempts >= 12) {
        pendingMobileFeedFrameRef.current = 0;
        setViewModeWithTransition('feed');
        return;
      }

      pendingMobileFeedFrameRef.current = window.requestAnimationFrame(tick);
    };

    tick();
  }, [setViewModeWithTransition]);

  const handleMobileControlChange = useCallback(
    (nextValue: MobileSrpControlValue) => {
      cancelPendingMobileFeedTransition();

      if (nextValue === mobileControlValue) return;

      emitTrackToggleViewModeEvent({ targetView: nextValue === 'grid' ? 'list' : nextValue });

      if (nextValue === 'map') {
        openMobileMap();
        return;
      }

      if (nextValue === 'feed' && !mobileMapActive) {
        switchToFeedAfterWindowTop();
      } else if (mobileMapActive) {
        setViewMode(nextValue);
      } else {
        setViewModeWithTransition(nextValue);
      }

      if (mobileMapActive) {
        closeMobileMap();
      }
    },
    [
      cancelPendingMobileFeedTransition,
      closeMobileMap,
      mobileMapActive,
      mobileControlValue,
      openMobileMap,
      setViewMode,
      setViewModeWithTransition,
      switchToFeedAfterWindowTop,
    ],
  );

  return (
    <div
      id="srp-shell"
      data-srp-component="shell"
      data-srp-view-mode={effectiveViewMode}
      data-mobile-map-active={mobileMapActive ? 'true' : 'false'}
      className={searchLayout}
    >
      <div
        id="srp-map-panel"
        data-srp-component="map-panel"
        className={mobileMapActive ? mapPanelMobileActive : mapPanel}
        onPointerEnter={onActivate}
        onPointerDown={onActivate}
        onFocus={onActivate}
      >
        {showIsland ? <SearchMapIsland isMobileMapActive={mobileMapActive} onMapReady={onMapReady} /> : null}
        {showPreview && mapPreviewBounds ? (
          <div className={mapPreviewOverlay} data-map-ready={isMapReady ? 'true' : 'false'} aria-hidden="true">
            <StaticMapPreview bounds={mapPreviewBounds} onError={onPreviewError} />
          </div>
        ) : null}
      </div>

      <div
        id="srp-content-panel"
        className={cx(
          contentPanel,
          contentPanelDesktopBottomInset,
          mobileMapActive ? contentPanelMobileMapActive : undefined,
        )}
        data-search-content-panel
        data-srp-component="content-panel"
        data-srp-view-mode={effectiveViewMode}
        data-mobile-map-active={mobileMapActive ? 'true' : 'false'}
      >
        {mobileMapActive && mobileMapSearchBar ? (
          <div className={mobileMapSearchBarOverlay} data-srp-component="mobile-map-search-bar">
            {mobileMapSearchBar}
          </div>
        ) : null}
        <div className={contentPanelChildren} hidden={mobileMapActive}>
          {/* The sticky SRP search bar lives here (not in the page) so it survives the
              slug morph that remounts the page; it's hidden with its content children in
              mobile-map mode (the floating overlay above replaces it) and on HDP routes. */}
          {hdpRoute || !stickySearchBar ? null : <Fragment key="sticky-search-bar">{stickySearchBar}</Fragment>}
          {children}
        </div>
      </div>
      {shouldShowMobileControls ? (
        <MobileSrpBottomControls value={mobileControlValue} onValueChange={handleMobileControlChange} />
      ) : null}
    </div>
  );
}
